import { useState } from 'react';
import {
  Input,
  Tooltip,
  Icon,
  Button,
} from '@nazka/nazka.mapframe.components';
import { faCircleInfo, faClose, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { useMutation } from '@tanstack/react-query';

import { formatCompanyIdentificationNumber } from '@services/utils.service';
import { checkCompany } from '@services/company.service';

import './company-identification-number-input.styl';

function CompanyIdentificationNumberInput({
  className,
  companies,
  onChange,
}) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const strippedInputValue = inputValue?.replaceAll('.', '');
  const isValidInputValue = strippedInputValue?.length === 10;
  const alreadyAdded = companies?.some(company => company.id === strippedInputValue);

  const checkCompanyMutation = useMutation({
    mutationFn: async () => checkCompany(strippedInputValue),
    onSuccess: (company) => {
      setInputValue('');
      onChange([...companies, company]);
      setError('');
    },
    onError: () => {
      setError('Onderneming niet gevonden');
    },
  });

  return (
    <label className={`company-identification-number-input${className ? ` ${className}` : ''}`}>
      <span className={`company-identification-number-input__label${className ? ` ${className}__label` : ''}`}>
        Ondernemingsnummer(s)
        <Tooltip
          id="ondernemingsnummer-help"
          clickable
          tooltip={(
            <div className="tooltip">
              Het ondernemingsnummer van de Kruispuntbank voor Ondernemingen (KBO) wordt
              gebruikt om een onderneming te identificeren. Je kunt het ondernemingsnummer
              {'voor jouw onderneming opzoeken via '}
              <a target="_blank" href="https://kbopub.economie.fgov.be" rel="noreferrer">
                kbopub.economie.fgov.be
              </a>
              .
            </div>
          )}
        >
          <Icon
            icon={faCircleInfo}
            size="14px"
            rootElProps={{ 'data-tooltip-id': 'ondernemingsnummer-help' }}
          />
        </Tooltip>
      </span>
      <div className="company-identification-number-input__container">
        <Input
          className={`company-identification-number-input__input${className ? ` ${className}__input` : ''}`}
          value={inputValue}
          onInputChange={({ target }) => {
            setError('');
            setInputValue(formatCompanyIdentificationNumber(target?.value));
          }}
          placeholder="____.___.___"
        />
        <Button
          className="company-identification-number-input__add"
          onClick={checkCompanyMutation.mutate}
          disabled={!isValidInputValue || alreadyAdded}
          isLoading={checkCompanyMutation.isPending}
        >
          Voeg toe
        </Button>
      </div>
      {(error || alreadyAdded) && (
        <p className="company-identification-number-input__error">
          {error || 'Dit ondernemingsnummer is reeds toegevoegd.'}
        </p>
      )}
      {companies && companies.length > 0 && (
        <div className="company-identification-number-input__list">
          {companies.map((company) => {
            return (
              <div
                className="company-identification-number-input__list__item"
                title={`${company.id} - ${company.label}`}
                key={company.id}
              >
                <Button
                  className="company-identification-number-input__list__item__remove"
                  onClick={() => onChange(companies.filter(c => c.id !== company.id))}
                  layout="ghost"
                  hover={false}
                  icon={faClose}
                  themeColor="secondary"
                />
                {company.isBranch && (
                  <Tooltip
                    tooltip={(
                      <>
                        We hebben de naam van de onderneming niet gevonden, maar wel verschillende vestigingen.
                        <br />
                        We tonen hier de naam van 1 van de gevonden vestigingen.
                      </>
                    )}
                    id={`company-${company.id}-warning`}
                  >
                    <Icon
                      icon={faWarning}
                      size="16px"
                      rootElProps={{ 'data-tooltip-id': `company-${company.id}-warning` }}
                      className="company-identification-number-input__list__item__warning"
                    />
                  </Tooltip>
                )}
                <span>
                  {`${formatCompanyIdentificationNumber(company.id)} - ${company.label}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </label>
  );
}

export default CompanyIdentificationNumberInput;